import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError, qs } from '../api/client';
import { Badge, Bandeau, Carte, Kpi, Squelette, Vide } from '../components/ui';
import { dateFr, deviseReference, libelleMouvement, moisLong, montant } from '../lib/format';
import { T } from '../i18n';
import type { EncaissementsVoiture } from '../api/types';
import { BandeauxDecalage } from '../components/DecalageEncaissement';

type EtatJour = 'loue' | 'reserve' | 'entretien' | 'libre';

interface Location {
  reservationId: string; numero: string; client: string; statut: string; reglement: string;
  debut: string; fin: string; joursDansLeMois: number; genereMinor: number; tarifJourMinor: number;
  totalMinor: number; encaisseMinor: number; soldeMinor: number;
  contratId: string | null; contratNumero: string | null;
}
interface Mouvement {
  date: string; type: string; libelle: string; devise: string;
  montantMinor: number; contrevaleurMinor: number; mode: string;
  par: string; reservationNumero: string | null;
  decalage: 'avance' | 'retard' | 'annulee' | null;
  locationDebut: string | null; locationFin: string | null;
}
interface Depense {
  date: string; quoi: string; detail: string; montantMinor: number; aLaCharge: boolean;
}
interface Reponse extends EncaissementsVoiture {
  vehicule: {
    id: string; marque: string; modele: string; immatriculation: string;
    categorie: string; km: number; statut: string;
    tarifBasseSaisonMinor: number; tarifHauteSaisonMinor: number;
  };
  mois: string;
  jours: EtatJour[];
  joursLoues: number; joursReserves: number; joursEntretien: number; nbLocations: number;
  genereMinor: number; resteDuMinor: number;
  depenseMinor: number; netMinor: number;
  reserveApresMinor: number; reserveApresJours: number;
  locations: Location[]; mouvements: Mouvement[]; depenses: Depense[];
}

const LIBELLE_JOUR = (): Record<EtatJour, string> => T.caisseVehicule.etatsJour;

const moisFr = (m: string) => {
  const [a, n] = m.split('-').map(Number);
  return moisLong(new Date(a!, n! - 1, 1));
};
const decale = (m: string, pas: number) => {
  const [a, n] = m.split('-').map(Number);
  const d = new Date(a!, n! - 1 + pas, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const moisCourant = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * La caisse d'UNE voiture.
 *
 * La carte de la caisse dit combien la voiture a rapporte. Ici on ouvre le
 * chiffre : quelles locations l'ont fait, quel argent est reellement rentre,
 * et ce que la voiture a coute pendant ce temps.
 */
export default function CaisseVehicule() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const mois = params.get('mois') ?? moisCourant();
  const [d, setD] = useState<Reponse | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    setD(null); setErreur(null);
    api.get<Reponse>(`/caisse/voiture/${id}${qs({ mois })}`)
      .then(setD)
      .catch((e) => setErreur(e instanceof ApiError ? e.erreur : T.caisseVehicule.erreurChargement));
  }, [id, mois]);

  const allerA = (m: string) => { params.set('mois', m); setParams(params, { replace: true }); };

  const tx = T.caisseVehicule;
  if (erreur) return <Bandeau type="ko" titre={tx.erreurTitre}>{erreur}</Bandeau>;
  if (!d) return <Squelette lignes={8} />;

  const v = d.vehicule;
  /* Le numéro du jour (0 = dimanche) sert au week-end ; le nom suit la langue. */
  const numJour = (i: number) => {
    const [a, n] = d.mois.split('-').map(Number);
    return new Date(a!, n! - 1, i + 1).getDay();
  };
  const jourSemaine = (i: number) => tx.jours[numJour(i)]!;
  const etatsJour = LIBELLE_JOUR();
  const devise = deviseReference();

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>{v.marque} {v.modele}</h1>
          <p>
            {v.immatriculation} · {v.categorie} · {v.km.toLocaleString('fr-FR')} km ·
            {tx.tarifs(montant(v.tarifHauteSaisonMinor), montant(v.tarifBasseSaisonMinor))}
          </p>
        </div>
        <div className="actions">
          {/* Retour sur le MÊME mois : c'est celui qu'on était en train de comparer. */}
          <Link className="btn btn-s" to={`/caisse?mois=${d.mois}`}>{tx.toutesVoitures}</Link>
          <button className="btn btn-s" onClick={() => allerA(decale(mois, -1))}>‹</button>
          <span className="btn btn-s" style={{ pointerEvents: 'none', minWidth: 130, justifyContent: 'center' }}>
            {moisFr(d.mois)}
          </span>
          <button className="btn btn-s" onClick={() => allerA(decale(mois, 1))}>›</button>
        </div>
      </div>

      <div className="grille g4" style={{ marginBottom: 16 }}>
        <Kpi libelle={tx.kpi.genere} valeur={montant(d.genereMinor)} className="signature-kpi"
             detail={tx.kpi.genereDetail(d.joursLoues, d.nbLocations)} />
        <Kpi libelle={tx.kpi.encaisse} valeur={montant(d.encaisseMinor)}
             detail={d.avance.length > 0
               ? tx.kpi.encaisseAvance(montant(d.avance.reduce((t, p) => t + p.montantMinor, 0)))
               : d.dejaEncaisse.length > 0
                 /* Sur téléphone, le bandeau est sous les quatre chiffres : le zéro doit s'expliquer seul. */
                 ? tx.kpi.encaisseDeja(montant(d.dejaEncaisse.reduce((t, p) => t + p.montantMinor, 0)))
                 : tx.kpi.encaisseDetail} />
        <Kpi libelle={tx.kpi.resteDu} valeur={montant(d.resteDuMinor)}
             detail={d.resteDuMinor > 0 ? tx.kpi.resteDuDetail : tx.kpi.toutEncaisse} />
        <Kpi libelle={tx.kpi.cout} valeur={montant(d.depenseMinor)}
             detail={tx.kpi.net(montant(d.netMinor))} />
      </div>

      {/* L'argent qui n'est pas entré dans le mois de sa location : où il est. */}
      <BandeauxDecalage e={d} mois={d.mois} versMois={allerA} />

      {d.reserveApresJours > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Bandeau type="accent" titre={tx.reserveApres.titre(d.reserveApresJours)}>
            <b>{montant(d.reserveApresMinor)}</b>{tx.reserveApres.texte}
          </Bandeau>
        </div>
      )}

      {/* Le calendrier n'a pas besoin de la moitie de la page ; le tableau des
        * locations, si — sinon sa colonne « solde » sort de l'ecran. */}
      <div className="grille" style={{
        gridTemplateColumns: 'minmax(0, 320px) minmax(0, 1fr)',
        alignItems: 'start', marginBottom: 20,
      }}>
        <Carte titre={tx.moisVoiture(moisFr(d.mois))}>
          <div className="legende" style={{ marginBottom: 10 }}>
            <span><i style={{ background: 'var(--succes)' }} />{tx.legende.louee}</span>
            <span><i style={{ background: 'var(--violet)' }} />{tx.legende.reservee}</span>
            <span><i style={{ background: '#8A929E' }} />{tx.legende.entretien}</span>
            <span><i style={{ background: 'var(--danger-doux)', border: '1px solid var(--danger-bord)' }} />{tx.legende.libre}</span>
          </div>
          <ol className="cal-v">
            {d.jours.map((e, i) => (
              <li key={i}
                  className={`${e !== 'libre' ? e : ''}${[0, 6].includes(numJour(i)) ? ' we' : ''}`}>
                <span className="cal-n">{i + 1}</span>
                <span className="cal-s">{jourSemaine(i)}</span>
                <span className="cal-b" title={`${i + 1} — ${etatsJour[e]}`} />
              </li>
            ))}
          </ol>
          <div className="rv-pied" style={{ marginTop: 10 }}>
            <span>
              {tx.pied.loues(d.joursLoues)}
              {d.joursReserves > 0 && tx.pied.reserves(d.joursReserves)}
              {d.joursEntretien > 0 && tx.pied.garage(d.joursEntretien)}
            </span>
          </div>
        </Carte>

        <Carte titre={tx.locationsMois} pad={false}>
          {d.locations.length === 0 ? (
            <div className="carte-p"><Vide titre={tx.pasSortie} /></div>
          ) : (
            <div className="enveloppe-tbl">
              <table className="tbl compact">
                <thead>
                  <tr>
                    <th>{tx.colonnesLocations.dossier}</th><th>{tx.colonnesLocations.client}</th><th className="droite">{tx.colonnesLocations.jours}</th>
                    <th className="droite">{tx.colonnesLocations.total}</th><th className="droite">{tx.colonnesLocations.encaisse}</th>
                    <th className="droite">{tx.colonnesLocations.solde}</th>
                  </tr>
                </thead>
                <tbody>
                  {d.locations.map((l) => (
                    <tr key={l.reservationId}>
                      <td className="mini">
                        <Link to={`/reservations/${l.reservationId}`}>{l.numero}</Link>
                        <div className="sourdine">
                          <span style={{ whiteSpace: 'nowrap' }}>{dateFr(l.debut)} → {dateFr(l.fin)}</span>
                          {l.reglement === 'differe' && tx.aCredit}
                        </div>
                      </td>
                      <td className="mini">{l.client}</td>
                      <td className="droite num">{l.joursDansLeMois}</td>
                      <td className="droite montant">{montant(l.totalMinor)}</td>
                      <td className="droite montant sourdine">{montant(l.encaisseMinor)}</td>
                      <td className="droite montant"
                          style={l.soldeMinor > 0 ? { color: 'var(--danger)', fontWeight: 700 } : undefined}>
                        {l.soldeMinor > 0 ? montant(l.soldeMinor) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Carte>
      </div>

      <Carte titre={tx.mouvements(d.mouvements.length)} pad={false}>
        {d.mouvements.length === 0 ? (
          <div className="carte-p"><Vide titre={tx.aucunMouvement} /></div>
        ) : (
          <div className="enveloppe-tbl">
            <table className="tbl compact">
              <thead>
                <tr>
                  <th>{tx.colonnesMouvements.date}</th><th>{tx.colonnesMouvements.type}</th><th>{tx.colonnesMouvements.quoi}</th>
                  <th>{tx.colonnesMouvements.par}</th><th className="droite">{tx.colonnesMouvements.recu}</th><th className="droite">{tx.colonnesMouvements.enDevise(devise)}</th>
                </tr>
              </thead>
              <tbody>
                {d.mouvements.map((m, i) => (
                  <tr key={i}>
                    <td className="mini num">{dateFr(m.date)}</td>
                    <td>
                      <Badge statut={m.type.startsWith('caution') ? 'retour'
                        : ['depense', 'retrait'].includes(m.type) ? 'retard' : 'en_cours'}
                             libelle={libelleMouvement(m.type)} />
                    </td>
                    <td className="mini">
                      {m.libelle}
                      {m.reservationNumero && <div className="sourdine">{tx.dossier(m.reservationNumero)}</div>}
                      {m.decalage === 'avance' && m.locationDebut && (
                        <div style={{ color: 'var(--violet)', fontWeight: 600 }}>
                          {tx.avance(dateFr(m.locationDebut), dateFr(m.locationFin!))}
                        </div>
                      )}
                      {m.decalage === 'retard' && m.locationDebut && (
                        <div className="sourdine">
                          {tx.retard(dateFr(m.locationDebut), dateFr(m.locationFin!))}
                        </div>
                      )}
                      {m.decalage === 'annulee' && (
                        <div className="sourdine">{tx.annulee}</div>
                      )}
                    </td>
                    <td className="mini">{m.par}</td>
                    <td className="droite montant sourdine">
                      {m.devise === devise ? '—' : montant(m.montantMinor, m.devise)}
                    </td>
                    <td className="droite montant">{montant(m.contrevaleurMinor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Carte>

      <div style={{ marginTop: 20 }}>
        <Carte titre={tx.cout(montant(d.depenseMinor))} pad={false}>
          {d.depenses.length === 0 ? (
            <div className="carte-p"><Vide titre={tx.aucuneDepense} /></div>
          ) : (
            <div className="enveloppe-tbl">
              <table className="tbl compact">
                <thead>
                  <tr><th>{tx.colonnesDepenses.date}</th><th>{tx.colonnesDepenses.quoi}</th><th>{tx.colonnesDepenses.detail}</th><th className="droite">{tx.colonnesDepenses.montant}</th></tr>
                </thead>
                <tbody>
                  {d.depenses.map((x, i) => (
                    <tr key={i}>
                      <td className="mini num">{dateFr(x.date)}</td>
                      <td>{x.quoi}</td>
                      <td className="mini sourdine">
                        {x.detail}
                        {!x.aLaCharge && <b style={{ color: 'var(--succes)' }}>{tx.refacturee}</b>}
                      </td>
                      <td className="droite montant"
                          style={x.aLaCharge ? { color: 'var(--danger)' } : { color: 'var(--texte3)' }}>
                        {montant(x.montantMinor)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Carte>
      </div>

      <p className="mini sourdine" style={{ marginTop: 12, maxWidth: 820 }}>
        {tx.note}
      </p>
    </>
  );
}
